// What the "Run AI again" strip says about a referral, from the rerun endpoint's
// state (AiReruns::State#as_json). Extracted so the wording rules can be tested
// without mounting the viewer.

export const AI_RERUN_IN_PROGRESS_CODE = 'ai_run_in_progress';

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// The strip's mode. `null` renders nothing.
export const aiRerunMode = (state) => {
  if (!state) return null;
  if (state.running) return 'running';
  if (state.eligible) {
    if (state.last_run?.status === 'failed') return 'failed';
    return state.document_changed ? 'changed' : 'retry';
  }
  if (state.last_run?.status === 'successful') return 'done';
  return null;
};

// "Updated 3 fields · kept 1 you edited". Kept fields are ones a person had
// edited, which the run deliberately left alone (AiReruns::Applier).
export const aiRerunSummary = (lastRun) => {
  if (!lastRun) return null;

  const updated = (lastRun.updated_fields || []).length;
  const kept = (lastRun.kept_fields || []).length;
  if (!updated && !kept) return 'AI ran again · nothing new found';

  const parts = [`Updated ${plural(updated, 'field')}`];
  if (kept) parts.push(`kept ${kept} you edited`);
  return parts.join(' · ');
};

export const aiRerunHint = (state) => {
  switch (aiRerunMode(state)) {
    case 'running':
      return 'AI is reading this document…';
    case 'failed':
      return 'AI run failed';
    case 'changed':
      return state.pages_added > 0
        ? `${plural(state.pages_added, 'page')} added since AI ran`
        : 'Document edited since AI ran';
    case 'retry':
      return 'AI did not finish on this document';
    case 'done':
      return aiRerunSummary(state.last_run);
    default:
      return null;
  }
};

export const aiRerunActionLabel = (state) => {
  const mode = aiRerunMode(state);
  if (mode === 'failed' || mode === 'retry') return 'Retry';
  if (mode === 'changed') return 'Run AI again';
  return null;
};

// Hover text naming the fields behind the summary counts.
export const aiRerunSummaryDetail = (lastRun) => {
  if (!lastRun) return '';

  const names = (fields) => (fields || []).map((field) => field.label).join(', ');
  return [
    lastRun.updated_fields?.length ? `Updated: ${names(lastRun.updated_fields)}` : null,
    lastRun.kept_fields?.length ? `Kept (you edited): ${names(lastRun.kept_fields)}` : null,
  ].filter(Boolean).join('\n');
};

// `crud` resolves `{ ...axiosError, error: true }` on a non-2xx.
export const aiRerunStartError = (result) => {
  if (!result?.error) return null;
  if (result.response?.data?.error_code === AI_RERUN_IN_PROGRESS_CODE) {
    return { alreadyRunning: true, message: 'AI is already running on this document.' };
  }
  return {
    alreadyRunning: false,
    message: result.response?.data?.error || 'AI could not be started. Please try again.',
  };
};

// Create Chart prefills from the AI fields. When the pages changed after AI ran
// and nobody re-ran it, the prefill describes the old document.
export const aiRerunChartWarning = (state, sendToken) => {
  if (!state || !sendToken || state.send_token !== sendToken) return null;
  if (state.running) return 'AI is re-reading this document. Fields below may change once it finishes.';
  if (state.eligible && state.document_changed) {
    return 'This document was edited after AI ran, so these fields may be incomplete. Run AI again from the document to refresh them.';
  }
  return null;
};
